import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, Plus, RefreshCw, Ban } from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import StatusBadge from '@framework/components/StatusBadge';
import BaseModal from '@framework/components/BaseModal';
import FormField from '@framework/components/FormField';
import FormActions from '@framework/components/FormActions';
import ErrorAlert from '@framework/components/ErrorAlert';
import LoadingSpinner from '@framework/components/LoadingSpinner';

const DEFAULTS = {
  sitemap_url: '',
  url_list_text: '',
  url_filter: '/recipe/',
  concurrency: 3,
  delay_s: 1.0,
  limit_n: 0,
  dry_run: false,
};

function jobLabel(sitemap_url) {
  if (!sitemap_url || sitemap_url.startsWith('(url-list')) return sitemap_url || 'URL list';
  try { return new URL(sitemap_url).hostname; } catch { return sitemap_url; }
}

function ProgressBar({ processed, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return (
    <div className="mt-1">
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div
          className="bg-orange-500 h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        {pct}% — {(processed || 0).toLocaleString()} / {(total || 0).toLocaleString()}
      </p>
    </div>
  );
}

function fmt(n) {
  return n == null ? '—' : Number(n).toLocaleString();
}

function elapsed(startedAt) {
  if (!startedAt) return null;
  const secs = Math.floor((Date.now() - new Date(startedAt)) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

export default function SitemapImportsPage() {
  const { apiService: api } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [mode, setMode] = useState('sitemap'); // 'sitemap' | 'url_list'
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const pollRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    try {
      const data = await api.getSitemapJobs();
      setJobs(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load import jobs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'running' || j.status === 'pending');
    if (hasActive) {
      pollRef.current = setInterval(fetchJobs, 4000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [jobs, fetchJobs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    let body = {
      concurrency: Number(form.concurrency),
      delay_s: Number(form.delay_s),
      limit_n: Number(form.limit_n),
      dry_run: form.dry_run,
    };

    if (mode === 'url_list') {
      const urls = form.url_list_text.split('\n').map(u => u.trim()).filter(Boolean);
      if (!urls.length) { setFormError('Paste at least one URL'); return; }
      body.url_list = urls;
    } else {
      if (!form.sitemap_url.trim()) { setFormError('Sitemap URL is required'); return; }
      body.sitemap_url = form.sitemap_url.trim();
      body.url_filter = form.url_filter;
    }

    setSubmitting(true);
    try {
      await api.createSitemapJob(body);
      setShowForm(false);
      setForm(DEFAULTS);
      setMode('sitemap');
      await fetchJobs();
    } catch (err) {
      setFormError(err.message || 'Failed to start import');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (jobId) => {
    setCancellingId(jobId);
    try {
      await api.cancelSitemapJob(jobId);
      await fetchJobs();
    } catch (err) {
      setError(err.message || 'Failed to cancel job');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Globe className="h-6 w-6 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recipe Imports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Bulk-import recipes from site sitemaps via JSON-LD
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchJobs}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => { setShowForm(true); setFormError(null); setMode('sitemap'); }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            New Import
          </button>
        </div>
      </div>

      {error && <ErrorAlert message={error} onDismiss={() => setError(null)} className="mb-4" />}

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Globe className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">No imports yet</p>
          <p className="text-sm mt-1">Click "New Import" to start a sitemap import job.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                {['Sitemap', 'Status', 'Progress', 'Imported', 'Skipped', 'Errors', 'Elapsed', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map(job => (
                <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3 max-w-xs">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={job.sitemap_url}>
                      {jobLabel(job.sitemap_url)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                      {job.url_filter}
                      {job.limit_n > 0 && ` · max ${job.limit_n.toLocaleString()}`}
                      {job.dry_run && ' · dry-run'}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={job.status} variant="dot" />
                    {job.status === 'failed' && job.error_message && (
                      <p className="text-xs text-red-400 mt-1 max-w-[220px] break-words">
                        {job.error_message}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[140px]">
                    {job.status === 'running' && job.total_urls ? (
                      <ProgressBar processed={job.processed} total={job.total_urls} />
                    ) : job.status === 'completed' ? (
                      <div className="w-full bg-green-100 dark:bg-green-900/30 rounded-full h-1.5">
                        <div className="bg-green-500 h-1.5 rounded-full w-full" />
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">
                    {fmt(job.imported)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {fmt(job.skipped)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-red-500 dark:text-red-400">
                    {job.errors > 0 ? fmt(job.errors) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
                    {job.status === 'running'
                      ? elapsed(job.started_at)
                      : job.completed_at
                        ? new Date(job.completed_at).toLocaleDateString()
                        : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    {(job.status === 'pending' || job.status === 'running') && (
                      <button
                        onClick={() => handleCancel(job.id)}
                        disabled={cancellingId === job.id}
                        className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                      >
                        <Ban className="h-3.5 w-3.5" />
                        {cancellingId === job.id ? 'Cancelling…' : 'Cancel'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BaseModal isOpen={showForm} onClose={() => { setShowForm(false); setMode('sitemap'); }} title="New Recipe Import">
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && <ErrorAlert message={formError} onDismiss={() => setFormError(null)} />}

          {/* Mode toggle */}
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
            {[['sitemap', 'Sitemap URL'], ['url_list', 'URL List']].map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`flex-1 py-2 font-medium transition-colors ${
                  mode === m
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'sitemap' ? (
            <>
              <FormField
                label="Sitemap URL"
                required
                value={form.sitemap_url}
                onChange={e => setForm(f => ({ ...f, sitemap_url: e.target.value }))}
                placeholder="https://www.example.com/sitemap.xml"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="URL filter"
                  value={form.url_filter}
                  onChange={e => setForm(f => ({ ...f, url_filter: e.target.value }))}
                  placeholder="/recipe/"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recipe URLs <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={8}
                value={form.url_list_text}
                onChange={e => setForm(f => ({ ...f, url_list_text: e.target.value }))}
                placeholder={"https://www.example.com/recipes/pasta\nhttps://www.example.com/recipes/soup\n…one URL per line"}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                One URL per line. Use this when the site blocks sitemap fetches from cloud IPs.
              </p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <FormField
              label="Concurrency"
              type="number"
              min={1}
              max={10}
              value={form.concurrency}
              onChange={e => setForm(f => ({ ...f, concurrency: e.target.value }))}
            />
            <FormField
              label="Delay (s)"
              type="number"
              min={0}
              max={10}
              step={0.1}
              value={form.delay_s}
              onChange={e => setForm(f => ({ ...f, delay_s: e.target.value }))}
            />
            <FormField
              label="Limit (0 = all)"
              type="number"
              min={0}
              value={form.limit_n}
              onChange={e => setForm(f => ({ ...f, limit_n: e.target.value }))}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.dry_run}
              onChange={e => setForm(f => ({ ...f, dry_run: e.target.checked }))}
              className="w-4 h-4 text-orange-600 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Dry run — parse and fetch but skip DB writes
            </span>
          </label>

          <FormActions
            onCancel={() => { setShowForm(false); setMode('sitemap'); }}
            submitLabel={submitting ? 'Starting…' : 'Start Import'}
            isSubmitting={submitting}
          />
        </form>
      </BaseModal>
    </div>
  );
}
